import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InquiryDialog({ open, onOpenChange, product }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        product_id: product?.id || "",
        product_title: product?.title || "",
        ...form,
      });
      toast.success("Inquiry sent successfully! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
      onOpenChange(false);
    } catch {
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A12] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white font-bold text-xl">Send Inquiry</DialogTitle>
          <DialogDescription className="text-[#9CA3AF]">
            {product ? `Inquiring about: ${product.title}` : "Send us your inquiry"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="inquiry-form">
          <div>
            <Input
              data-testid="inquiry-name"
              placeholder="Your Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-[#0A0A12] border-white/20 focus:border-[#DC143C] text-white placeholder:text-[#9CA3AF]/60"
            />
          </div>
          <div>
            <Input
              data-testid="inquiry-email"
              type="email"
              placeholder="Email Address *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-[#0A0A12] border-white/20 focus:border-[#DC143C] text-white placeholder:text-[#9CA3AF]/60"
            />
          </div>
          <div>
            <Input
              data-testid="inquiry-phone"
              placeholder="Phone Number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="bg-[#0A0A12] border-white/20 focus:border-[#DC143C] text-white placeholder:text-[#9CA3AF]/60"
            />
          </div>
          <div>
            <Textarea
              data-testid="inquiry-message"
              placeholder="Your Message *"
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="bg-[#0A0A12] border-white/20 focus:border-[#DC143C] text-white placeholder:text-[#9CA3AF]/60 resize-none"
            />
          </div>
          <Button
            type="submit"
            data-testid="inquiry-submit"
            disabled={submitting}
            className="w-full bg-[#DC143C] text-white hover:bg-[#B01030] font-semibold tracking-wide py-3"
          >
            {submitting ? "Sending..." : (
              <span className="flex items-center gap-2">
                <Send size={16} /> Send Inquiry
              </span>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
